// src/pages/workflows/WorkflowDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Breadcrumbs,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  ArrowBack as BackIcon,
  SkipNext as SkipIcon,
  Refresh as RestartIcon,
  NavigateNext as NextIcon,
  History as HistoryIcon,
  EventNote as NoteIcon,
} from '@mui/icons-material';

// Import custom components
import { MagicLoader } from '../../design-system';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import StatusChip from '../../components/ui/StatusChip';

// Import hooks and stores
import useWorkflowStore from '../../stores/workflowStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';

const WorkflowDetail = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    currentInstance, 
    fetchInstanceById,
    advanceWorkflow,
    rejectWorkflow,
    skipWorkflowStep,
    cancelWorkflow,
    restartWorkflow,
    isLoading, 
    error 
  } = useWorkflowStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const { user, hasPermission } = useAuthStore();
  
  // Local state
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  
  // Check permissions
  const canUpdate = hasPermission('workflows', 'update');
  
  // Load workflow instance details
  useEffect(() => {
    const loadWorkflow = async () => {
      try {
        await fetchInstanceById(id);
      } catch (error) {
        showError(error.message || 'Failed to load workflow details');
      }
    };
    
    loadWorkflow();
  }, [fetchInstanceById, id]);
  
  // Check if current user is assigned to the current step
  const isCurrentUserAssigned = () => {
    if (!currentInstance || !currentInstance.currentStep || !user) return false;
    return currentInstance.currentStep.assignedTo?._id === user._id;
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Get the status of a step based on the current instance state
  const getStepStatus = (step, index) => {
    if (!currentInstance) return 'inactive';
    
    // Current step
    if (currentInstance.currentStepIndex === index) {
      return 'active';
    }
    
    // Past steps
    if (index < currentInstance.currentStepIndex) {
      return step.status || 'completed';
    }
    
    // Future steps
    return 'inactive';
  };
  
  // Open confirmation dialog for various actions
  const openConfirmDialog = (action, title, message) => {
    setConfirmAction(action);
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmDialogOpen(true);
  };
  
  // Handle workflow actions
  const handleAdvance = async () => {
    setAdvanceDialogOpen(false);
    
    try {
      await advanceWorkflow(id, { comment });
      showSuccess('Workflow step approved successfully');
      setComment('');
    } catch (error) {
      showError(error.message || 'Failed to approve workflow step');
    }
  };
  
  const handleReject = async () => {
    setRejectDialogOpen(false);
    
    try {
      await rejectWorkflow(id, { comment });
      showSuccess('Workflow step rejected');
      setComment('');
    } catch (error) {
      showError(error.message || 'Failed to reject workflow step');
    }
  };
  
  const handleSkip = async () => {
    setConfirmDialogOpen(false);
    
    try {
      await skipWorkflowStep(id, { comment });
      showSuccess('Workflow step skipped successfully');
    } catch (error) {
      showError(error.message || 'Failed to skip workflow step');
    }
  };
  
  const handleCancel = async () => {
    setConfirmDialogOpen(false);
    
    try {
      await cancelWorkflow(id, { comment });
      showSuccess('Workflow cancelled successfully');
      navigate('/workflows/instances');
    } catch (error) {
      showError(error.message || 'Failed to cancel workflow');
    }
  };
  
  const handleRestart = async () => {
    setConfirmDialogOpen(false);
    
    try {
      await restartWorkflow(id);
      showSuccess('Workflow restarted successfully');
    } catch (error) {
      showError(error.message || 'Failed to restart workflow');
    }
  };
  
  // Handle confirm dialog actions
  const handleConfirmAction = () => {
    switch (confirmAction) {
      case 'skip':
        handleSkip();
        break;
      case 'cancel':
        handleCancel();
        break;
      case 'restart':
        handleRestart();
        break;
      default:
        setConfirmDialogOpen(false);
    }
  };
  
  // Render the entity link based on type
  const renderEntityLink = () => {
    if (!currentInstance) return null;
    
    let link = '#';
    let linkText = currentInstance.entityName || 'N/A';
    
    if (currentInstance.entityType === 'content' && currentInstance.entityId) {
      link = `/content/${currentInstance.entityId}`;
    } else if (currentInstance.entityType === 'campaign' && currentInstance.entityId) {
      link = `/campaigns/${currentInstance.entityId}`;
    } else if (currentInstance.entityType === 'form' && currentInstance.entityId) {
      link = `/forms/submissions/${currentInstance.entityId}`;
    }
    
    return (
      <Link 
        component={RouterLink} 
        to={link} 
        underline="hover" 
        
      >
        {linkText}
      </Link>
    );
  };
  
  // Render status badge
  const renderStatusBadge = () => {
    if (!currentInstance) return null;
    
    switch (currentInstance.status) {
      case 'pending':
        return <StatusChip label="Pending" color="warning" />;
      case 'inProgress':
        return <StatusChip label="In Progress" color="info" />;
      case 'completed':
        return <StatusChip label="Completed" color="success" />;
      case 'rejected':
        return <StatusChip label="Rejected" color="error" />;
      case 'cancelled':
        return <StatusChip label="Cancelled" color="default" />;
      default:
        return <StatusChip label={currentInstance.status || 'Unknown'} color="default" />;
    }
  };
  
  // Loading state
  if (isLoading && !currentInstance) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <MagicLoader variant="magical" size="large" message="Loading workflow details..." />
      </Box>
    );
  }
  
  // Error state
  if (error && !currentInstance) {
    return (
      <Box sx={{ py: 3 }}>
        <Paper sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Workflow
          </Typography>
          <Typography variant="body1" paragraph>
            {error}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/workflows/instances')}
          >
            Back to Workflows
          </Button>
        </Paper>
      </Box>
    );
  }
  
  // If workflow instance is not loaded yet
  if (!currentInstance) {
    return null;
  }
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs 
        separator={<NextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link 
          component={RouterLink} 
          to="/workflows/instances" 
          underline="hover" 
          color="inherit"
        >
          Workflows
        </Link>
        <Typography color="text.primary">
          {currentInstance.name || 'Workflow Details'}
        </Typography>
      </Breadcrumbs>
      
      {/* Header with workflow info and actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h5" component="h1">
                {currentInstance.name}
              </Typography>
              {renderStatusBadge()}
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {currentInstance.description || 'No description provided'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                  <Typography variant="body2">
                    <strong>Type:</strong> {currentInstance.type}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                  <Typography variant="body2">
                    <strong>Related Entity:</strong> {currentInstance.entityType} - {renderEntityLink()}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                  <Typography variant="body2">
                    <strong>Started:</strong> {formatDate(currentInstance.createdAt)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                  <Typography variant="body2">
                    <strong>Initiated By:</strong> {currentInstance.createdBy?.name || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'flex-end', height: '100%' }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button
                  variant="outlined"
                  startIcon={<BackIcon />}
                  onClick={() => navigate('/workflows/instances')}
                >
                  Back
                </Button>
                
                {/* Show actions based on workflow status and user permissions */}
                {isCurrentUserAssigned() && currentInstance.status === 'inProgress' && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ApproveIcon />}
                      onClick={() => setAdvanceDialogOpen(true)}
                      magical={true}
                    >
                      Approve
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<RejectIcon />}
                      onClick={() => setRejectDialogOpen(true)}
                    >
                      Reject
                    </Button>
                  </>
                )}
                
                {/* Admin actions */}
                {canUpdate && currentInstance.status === 'inProgress' && (
                  <Tooltip title="Skip current step">
                    <IconButton
                      
                      onClick={() => openConfirmDialog(
                        'skip',
                        'Skip Current Step',
                        'Are you sure you want to skip the current step? This will move the workflow to the next step without approval.'
                      )}
                    >
                      <SkipIcon />
                    </IconButton>
                  </Tooltip>
                )}
                
                {canUpdate && ['inProgress', 'pending'].includes(currentInstance.status) && (
                  <Tooltip title="Cancel workflow">
                    <IconButton
                      color="error"
                      onClick={() => openConfirmDialog(
                        'cancel',
                        'Cancel Workflow',
                        'Are you sure you want to cancel this workflow? This action cannot be undone.'
                      )}
                    >
                      <RejectIcon />
                    </IconButton>
                  </Tooltip>
                )}
                
                {canUpdate && ['completed', 'rejected', 'cancelled'].includes(currentInstance.status) && (
                  <Tooltip title="Restart workflow">
                    <IconButton
                      
                      onClick={() => openConfirmDialog(
                        'restart',
                        'Restart Workflow',
                        'Are you sure you want to restart this workflow? This will reset all steps and start from the beginning.'
                      )}
                    >
                      <RestartIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Workflow Steps */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Workflow Steps
            </Typography>
            
            <Stepper 
              activeStep={currentInstance.currentStepIndex} 
              orientation="vertical"
              sx={{ mt: 3 }}
            >
              {currentInstance.steps.map((step, index) => {
                const stepStatus = getStepStatus(step, index);
                let stepIcon = null;
                
                if (step.status === 'completed') {
                  stepIcon = <ApproveIcon color="success" />;
                } else if (step.status === 'rejected') {
                  stepIcon = <RejectIcon color="error" />;
                } else if (step.status === 'skipped') {
                  stepIcon = <SkipIcon color="warning" />;
                }
                
                return (
                  <Step key={index} completed={stepStatus === 'completed' || step.status === 'completed'}>
                    <StepLabel 
                      error={step.status === 'rejected'}
                      icon={stepIcon}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1">
                          {step.name}
                        </Typography>
                        
                        {step.status && (
                          <StatusChip 
                            label={step.status.charAt(0).toUpperCase() + step.status.slice(1)} 
                            color={
                              step.status === 'completed' ? 'success' : 
                              step.status === 'rejected' ? 'error' : 
                              step.status === 'skipped' ? 'warning' : 
                              'default'
                            }
                            size="small"
                          />
                        )}
                      </Box>
                    </StepLabel>
                    
                    <StepContent>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {step.description || 'No description provided'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Assigned to:</strong> {step.assignedTo?.name || 'N/A'}
                        </Typography>
                        
                        {step.dueDate && (
                          <Typography variant="body2">
                            <strong>Due date:</strong> {formatDate(step.dueDate)}
                          </Typography>
                        )}
                      </Box>
                      
                      {step.comment && (
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            mb: 2, 
                            bgcolor: alpha(theme.palette.background.default, 0.5) 
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Comment:</strong>
                          </Typography>
                          <Typography variant="body2">
                            {step.comment}
                          </Typography>
                        </Paper>
                      )}
                      
                      {step.actionDate && (
                        <Typography variant="caption" color="text.secondary">
                          {step.status === 'completed' ? 'Approved' : 
                           step.status === 'rejected' ? 'Rejected' : 
                           step.status === 'skipped' ? 'Skipped' : 'Actioned'} on {formatDate(step.actionDate)}
                        </Typography>
                      )}
                    </StepContent>
                  </Step>
                );
              })}
            </Stepper>
            
            {currentInstance.steps.length === 0 && (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No steps defined for this workflow
              </Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Workflow History */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ mr: 1 }} />
              Activity History
            </Typography>
            
            <List sx={{ mt: 2 }}>
              {currentInstance.history && currentInstance.history.length > 0 ? (
                currentInstance.history.map((item, index) => (
                  <ListItem 
                    key={index}
                    sx={{ 
                      py: 1,
                      borderLeft: `3px solid ${
                        item.action === 'created' ? theme.palette.info.main :
                        item.action === 'advanced' ? theme.palette.success.main :
                        item.action === 'rejected' ? theme.palette.error.main :
                        item.action === 'skipped' ? theme.palette.warning.main :
                        theme.palette.primary.main
                      }`,
                      pl: 2,
                      mb: 1,
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      borderRadius: '0 4px 4px 0',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.action === 'created' ? 'Workflow created' :
                           item.action === 'advanced' ? 'Step approved' :
                           item.action === 'rejected' ? 'Step rejected' :
                           item.action === 'skipped' ? 'Step skipped' :
                           item.action === 'cancelled' ? 'Workflow cancelled' :
                           item.action === 'restarted' ? 'Workflow restarted' :
                           item.action}
                           {item.stepName ? `: ${item.stepName}` : ''}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="text.secondary" component="div">
                            By: {item.user?.name || 'System'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {formatDate(item.timestamp)}
                          </Typography>
                          {item.notes && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              component="div"
                              sx={{ 
                                mt: 1, 
                                p: 1, 
                                bgcolor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: 1,
                                fontStyle: 'italic'
                              }}
                            >
                              "{item.notes}"
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary="No activity recorded yet"
                    secondary="Actions taken on this workflow will appear here"
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Approval Dialog */}
      <Dialog open={advanceDialogOpen} onClose={() => setAdvanceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Workflow Step</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are approving the current step: <strong>{currentInstance.currentStep?.name}</strong>
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            id="comment"
            label="Approval Comment (Optional)"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvanceDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAdvance} 
            variant="contained" 
            color="success"
            startIcon={<ApproveIcon />}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Workflow Step</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are rejecting the current step: <strong>{currentInstance.currentStep?.name}</strong>
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            id="comment"
            label="Rejection Reason (Required)"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            error={rejectDialogOpen && comment.trim() === ''}
            helperText={rejectDialogOpen && comment.trim() === '' ? 'Please provide a reason for rejection' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleReject} 
            variant="contained" 
            color="error"
            startIcon={<RejectIcon />}
            disabled={comment.trim() === ''}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Confirmation Dialog for various actions */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmTitle}
        content={confirmMessage}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialogOpen(false)}
        confirmButtonColor={confirmAction === 'cancel' ? 'error' : 'primary'}
        isDelete={confirmAction === 'cancel'}
      />
    </Box>
  );
};

export default WorkflowDetail;